/**
 * The storefront API surface (M24).
 *
 * Two halves, and the split matters. **Browsing and registering are anonymous** -- no
 * bearer token, and `apiFetch`'s 401-refresh-and-replay dance never triggers because there
 * is no session to expire. `listed_on_marketplace` and `status` filtering happen on the
 * server (`marketplace/router.py`); nothing here can ask for more than a shopper is allowed
 * to see, because the fields simply are not in `StorefrontProduct` -- there is no `cost` to
 * accidentally render.
 *
 * **Buying carries the ordinary session.** A shopper signs in with the same bearer token
 * the ERP issues; what they do not have is a permission on anything, so every one of these
 * routes authorises on ownership rather than on the matrix. Nothing here sends a price:
 * the basket is a list of ids and quantities, and the server prices it.
 */

import type { Shipment } from '@/features/orders/ShipmentCard'
import { API_PREFIX, apiFetch, type Envelope, type Page } from '@/api/client'
import { formatMoney } from '@/lib/money'

export interface StorefrontProduct {
  id: string
  code: string
  name: string
  description: string | null
  long_description?: string | null
  category_id: string
  category_name: string | null
  unit_of_measure: string
  price: string
  /** What an order placed now charges per unit, when an online discount applies; null
   *  when none does. Worked out by the server with the rule it prices the order with. */
  sale_price?: string | null
  tax_rate: string
  status: string
  images: string[] | null
}

/** The price the shopper actually pays: the sale price when there is one. */
export const shopPrice = (product: Pick<StorefrontProduct, 'price' | 'sale_price'>): string =>
  product.sale_price ?? product.price

/** Whole-number percentage off, for the badge, or null when there is no sale price. */
export const percentOff = (
  product: Pick<StorefrontProduct, 'price' | 'sale_price'>,
): number | null => {
  if (product.sale_price == null) return null
  const list = Number(product.price)
  if (!(list > 0)) return null
  return Math.round((1 - Number(product.sale_price) / list) * 100)
}

export interface StorefrontCategory {
  id: string
  name: string
  parent_id: string | null
  children: StorefrontCategory[]
}

export interface StorefrontFilters {
  q?: string
  category_id?: string
  page?: number
  page_size?: number
}

const anon = { anonymous: true } as const

export const browseProducts = (filters: StorefrontFilters = {}) => {
  const query = new URLSearchParams({
    page: String(filters.page ?? 1),
    page_size: String(filters.page_size ?? 24),
  })
  if (filters.q) query.set('q', filters.q)
  if (filters.category_id) query.set('category_id', filters.category_id)
  return apiFetch<Page<StorefrontProduct>>(`${API_PREFIX}/marketplace/products?${query}`, anon)
}

export const getStorefrontProduct = (id: string) =>
  apiFetch<Envelope<StorefrontProduct>>(`${API_PREFIX}/marketplace/products/${id}`, anon).then(
    (envelope) => envelope.data,
  )

export const listStorefrontCategories = () =>
  apiFetch<Envelope<StorefrontCategory[]>>(`${API_PREFIX}/marketplace/categories`, anon).then(
    (envelope) => envelope.data,
  )

/** Same flattening as the internal catalog's dropdown -- see `inventory/api.ts` -- kept
 *  as its own copy because the two `Category`-shaped types are not the same type. */
export function flattenStorefrontCategories(
  nodes: StorefrontCategory[],
  depth = 0,
): { id: string; label: string }[] {
  return nodes.flatMap((node) => [
    { id: node.id, label: `${'— '.repeat(depth)}${node.name}` },
    ...flattenStorefrontCategories(node.children ?? [], depth + 1),
  ])
}

/** The shop's own terms: currency, delivery charge, and whether cards can be taken. */
export interface StorefrontSettings {
  store_name: string
  currency: string
  shipping_flat_rate: string
  shipping_free_over: string | null
  /**
   * False on a deployment still running the simulated payment provider. The checkout then
   * places the order and says so, rather than offering a pay button that cannot work.
   */
  payments_enabled: boolean
}

export const getStorefrontSettings = () =>
  apiFetch<Envelope<StorefrontSettings>>(`${API_PREFIX}/marketplace/settings`, anon).then(
    (envelope) => envelope.data,
  )

// ------------------------------------------------------------------------------- accounts

export interface StorefrontSignupInput {
  email: string
  full_name: string
  password: string
  dial_code?: string
  phone?: string | null
}

export interface StorefrontTokens {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

/**
 * Registers a shopper and returns a live session.
 *
 * Anonymous, obviously -- there is no session yet. The caller is responsible for adopting
 * the returned pair (`useAuth.adoptSession`); this function deliberately does not touch
 * global state, so a failed profile fetch afterwards cannot leave a half-signed-in app.
 */
export const signUpShopper = (input: StorefrontSignupInput) =>
  apiFetch<Envelope<StorefrontTokens>>(`${API_PREFIX}/marketplace/signup`, {
    ...anon,
    method: 'POST',
    body: JSON.stringify(input),
  }).then((envelope) => envelope.data)

// --------------------------------------------------------------------------------- orders

export interface ShippingAddress {
  name: string
  dial_code?: string
  phone?: string | null
  line1: string
  line2?: string | null
  city: string
  state?: string | null
  postal_code: string
  country: string
  /** Only used to pick the nearest site holding the goods; not stored on the order. */
  latitude?: number
  longitude?: number
}

export interface PlaceOrderInput {
  items: { product_id: string; quantity: number }[]
  shipping_address: ShippingAddress
  notes?: string | null
}

export interface StorefrontOrderItem {
  product_id: string
  name: string
  quantity: number
  unit_price: string
  tax_amount: string
  line_total: string
}

export interface StorefrontOrderSummary {
  id: string
  order_number: string
  order_date: string
  status: string
  payment_status: string
  /** Goods only. Delivery is `shipping_amount`; the card is charged `amount_due`. */
  total: string
  shipping_amount: string
  amount_due: string
}

export interface StorefrontOrder extends StorefrontOrderSummary, Shipment {
  subtotal: string
  discount_amount: string
  tax_amount: string
  shipping_address: ShippingAddress | null
  paid_at: string | null
  refunded_at: string | null
  cancelled_at: string | null
  items: StorefrontOrderItem[]
}

/** No prices in the body: the basket is ids and counts, and the server prices it. */
export const placeStorefrontOrder = (input: PlaceOrderInput) =>
  apiFetch<Envelope<StorefrontOrder>>(`${API_PREFIX}/marketplace/orders`, {
    method: 'POST',
    body: JSON.stringify(input),
  }).then((envelope) => envelope.data)

export const listMyOrders = (page = 1) =>
  apiFetch<Page<StorefrontOrderSummary>>(
    `${API_PREFIX}/marketplace/orders?page=${page}&page_size=20`,
  )

export const getMyOrder = (id: string) =>
  apiFetch<Envelope<StorefrontOrder>>(`${API_PREFIX}/marketplace/orders/${id}`).then(
    (envelope) => envelope.data,
  )

export interface StorefrontCheckoutSession {
  provider: string
  session_id: string
  url: string
  expires_at: string | null
}

/** Raises a hosted card page for an order already placed. The caller redirects to `url`. */
export const startStorefrontCheckout = (orderId: string) =>
  apiFetch<Envelope<StorefrontCheckoutSession>>(
    `${API_PREFIX}/marketplace/orders/${orderId}/checkout`,
    { method: 'POST' },
  ).then((envelope) => envelope.data)

/**
 * Settles an order from the shopper's return trip, without waiting for the webhook.
 *
 * The webhook is still what the API treats as the record, but it arrives on Stripe's
 * schedule and cannot reach a machine that is not on the public internet at all. The
 * redirect back carries the session id, and this hands it to the API, which asks Stripe
 * with its own key what became of it. Idempotent server-side: calling it on an order the
 * webhook already settled returns that order unchanged.
 */
export const confirmStorefrontPayment = (orderId: string, sessionId: string) =>
  apiFetch<Envelope<StorefrontOrder>>(
    `${API_PREFIX}/marketplace/orders/${orderId}/payment/confirm`,
    { method: 'POST', body: JSON.stringify({ session_id: sessionId }) },
  ).then((envelope) => envelope.data)

/**
 * Call off one of the shopper's own orders: refunded to the card if it was paid.
 *
 * Only until the shop approves it (`draft` or `submitted`); after that the API answers
 * `ORDER_IN_PROGRESS` and the shopper has to contact the shop.
 */
export const cancelMyOrder = (orderId: string, reason?: string) =>
  apiFetch<Envelope<StorefrontOrder>>(`${API_PREFIX}/marketplace/orders/${orderId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || null }),
  }).then((envelope) => envelope.data)

/** Where a shopper may still cancel on their own. Mirrors `SHOPPER_CANCELLABLE_STATUSES`. */
export const SHOPPER_CANCELLABLE = ['draft', 'submitted']

export { formatMoney }
